/* Ash — required-selection (PDP form gating)
 *
 * Behaviour on PDP load:
 *   - Every variant option (Size, Color, etc.) AND every extra option
 *     (e.g. Fit, via inputs with data-ash-required) starts UNSELECTED.
 *     We strip SSR-checked state and clear the `name="id"` hidden field.
 *   - Add to Cart + Buy Now (inline) are DISABLED until every required
 *     selection has a value.
 *   - The fly-to-cart animation that Horizon triggers on ATC click is
 *     suppressed while the button is disabled — we block the click in
 *     the capture phase so no listeners run.
 *   - We re-evaluate every change/click event so the user gets instant
 *     feedback as they pick options.
 *
 * Scope: only runs on the product template.
 */
(function () {
  if (!document.body || !document.querySelector('[data-template="product"]')) {
    /* Defer until body exists if the script is in <head> */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
      return;
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function init() {
    /* Only run on PDP. The template attribute is set by Horizon's layout. */
    if (!document.querySelector('[data-template="product"]')) return;

    /* 0. Permanently disable the fly-to-cart animation by clearing
     *    its data attribute on every add-to-cart-component. Also
     *    proactively remove any <fly-to-cart> elements that get
     *    created at runtime. */
    disableFlyToCart();
    var flyObserver = new MutationObserver(disableFlyToCart);
    flyObserver.observe(document.body, { childList: true, subtree: true });

    /* Title Case the Add To Cart button text — Horizon's translation
       strings come in mixed case (e.g. "Add to cart" / "ADD TO CART")
       and the user wants "Add To Cart" with every word capitalised.
       Re-run on every DOM mutation because the variant fragment
       rerender swaps the button text. */
    titleCaseAtc();
    var atcTextObserver = new MutationObserver(titleCaseAtc);
    atcTextObserver.observe(document.body, { childList: true, subtree: true, characterData: true });

    /* User-interaction flag — set on first real click/change in the
       main variant picker. Until the user actually picks something,
       any `checked` state that appears is Horizon re-applying SSR
       defaults and gets wiped. After the user has interacted, we
       leave their picks alone. */
    window.__ashUserPicked = false;

    /* 1. Clear any SSR-checked variant radios and any pre-checked
     *    required-extra radios. We do this BEFORE Horizon's
     *    variant-picker.js wires up its listeners, then again after to
     *    survive Horizon's "restore default" pass. */
    clearAllSelections();

    /* Re-clear at every common timing Horizon might re-apply defaults. */
    requestAnimationFrame(clearAllSelections);
    setTimeout(clearAllSelections, 60);
    setTimeout(clearAllSelections, 150);
    setTimeout(clearAllSelections, 300);
    setTimeout(clearAllSelections, 500);
    setTimeout(clearAllSelections, 1000);

    /* 2. Disable the ATC and Buy Now buttons initially, then attach
     *    listeners that re-evaluate on every change. */
    evaluateAndUpdate();
    document.addEventListener('change', onAnyChange, true);
    document.addEventListener('click', onAnyClick, true);

    /* 3. Suppress fly-to-cart animation while ATC is disabled. */
    document.addEventListener('click', interceptAtcClick, true);

    /* 3b. Form submit handler — catches submissions that bypass the
       button click (e.g. Enter key, programmatic .submit(), Horizon's
       custom on:click="/handleClick" routing). Blocks if anything is
       missing from the main PDP form. */
    document.addEventListener('submit', interceptAtcSubmit, true);

    /* 4. Re-evaluate button state when Horizon swaps form fragments.
       Debounced via rAF so a burst of mutations triggers only one
       evaluation; without this the message-rerender path would loop
       infinitely (re-render → observer → evaluate → re-render). */
    var pendingEval = false;
    var observer = new MutationObserver(function () {
      if (pendingEval) return;
      pendingEval = true;
      requestAnimationFrame(function () {
        pendingEval = false;
        evaluateAndUpdate();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function titleCase(s) {
    return String(s || '').toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function titleCaseAtc() {
    var btns = document.querySelectorAll(
      'main[data-template="product"] .product-form-buttons add-to-cart-component .button, ' +
      'main[data-template="product"] .product-form-buttons button[ref="addToCartButton"], ' +
      'main[data-template="product"] [ref="productFormButtons"] add-to-cart-component .button'
    );
    btns.forEach(function (btn) {
      var span = btn.querySelector('.add-to-cart-text') || btn.querySelector(':scope > span:not(.svg-wrapper)');
      var target = span || btn;
      var current = (target.textContent || '').trim();
      if (!current) return;
      var desired = titleCase(current);
      if (current !== desired) target.textContent = desired;
    });
  }

  function disableFlyToCart() {
    /* Disable fly-to-cart animation everywhere on the PDP (main +
       any quick-add components on related-product cards). The
       animation is purely visual; suppressing it on all forms is
       safe. */
    document.querySelectorAll('add-to-cart-component[data-add-to-cart-animation]').forEach(function (c) {
      c.setAttribute('data-add-to-cart-animation', 'none');
      c.setAttribute('data-add-to-cart-animation-disabled', '1');
    });
    document.querySelectorAll('fly-to-cart').forEach(function (el) { el.remove(); });
  }

  /* The fit selector (and possibly other extras) renders OUTSIDE
     the main product form — it uses form="<id>" to associate. So
     using the form as scope misses those inputs. Instead, we run a
     document-wide query and FILTER OUT anything inside a quick-add
     component or product card. The result is "everything on the
     main PDP that isn't part of a related-product card's quick-add". */
  function scopeQuery(selector) {
    var all = document.querySelectorAll(selector);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (!el.closest('quick-add-component, product-card, .product-card, .related-products-block, [class*="related-products"]')) {
        out.push(el);
      }
    }
    /* Return a NodeList-like array with forEach available */
    out.forEach = Array.prototype.forEach;
    return out;
  }

  function clearAllSelections() {
    /* CRITICAL: once the user has picked anything, the late-firing
       setTimeouts (300ms / 500ms / 1000ms) must NOT wipe their
       choice. Without this guard the user's pick would be silently
       cleared a moment after they tap. */
    if (window.__ashUserPicked) return;

    /* Variant option radios in the MAIN form only — Horizon SSRs
     * `checked` on the first available option. Quick-add forms on
     * related-product cards are untouched. */
    scopeQuery(
      'fieldset.variant-option input[type="radio"], fieldset[class*="variant-option--"] input[type="radio"]'
    ).forEach(function (r) {
      r.checked = false;
      if (r.hasAttribute('data-current-checked')) r.setAttribute('data-current-checked', 'false');
      if (r.hasAttribute('data-previous-checked')) r.setAttribute('data-previous-checked', 'false');
    });

    scopeQuery('input[data-ash-required]').forEach(function (r) {
      r.checked = false;
    });

    scopeQuery('fieldset.variant-option select, fieldset[class*="variant-option--"] select').forEach(function (s) {
      try { s.selectedIndex = -1; } catch (_) { s.value = ''; }
    });

    /* Empty out the hidden variant id in the main form so the form
       is genuinely empty. Horizon will re-populate it once the user
       makes a full pick. */
    scopeQuery('input[name="id"]').forEach(function (i) { i.value = ''; });
  }

  function onAnyChange(e) {
    if (!e.target || !isRelevantInput(e.target)) return;
    /* Real user change — stop the watchdog from undoing future picks. */
    if (e.isTrusted) window.__ashUserPicked = true;
    setTimeout(evaluateAndUpdate, 0);
  }

  function onAnyClick(e) {
    if (!e.target) return;
    var rel = e.target.closest('label.variant-option__button-label, label.ash-fit-selector__option, .swatch, .variant-option__button-label');
    if (!rel) return;
    if (e.isTrusted) window.__ashUserPicked = true;
    /* Mark this label .is-selected and clear siblings in the same
       fieldset/group so the visual state survives any Horizon
       re-render. CSS uses both :has(:checked) and .is-selected. */
    var fs = rel.closest('fieldset.variant-option, fieldset[class*="variant-option--"], .ash-fit-selector__group');
    if (fs) {
      fs.querySelectorAll('.is-selected').forEach(function (s) { s.classList.remove('is-selected'); });
      rel.classList.add('is-selected');
    }
    setTimeout(evaluateAndUpdate, 0);
  }

  function isRelevantInput(el) {
    if (!el.matches) return false;
    return el.matches(
      'fieldset.variant-option input, fieldset[class*="variant-option--"] input, [data-ash-required], fieldset.variant-option select, fieldset[class*="variant-option--"] select'
    );
  }

  function evaluateAndUpdate() {
    var missing = getMissingSelections();
    var allSelected = missing.length === 0;
    setButtonsDisabled(!allSelected);
    /* If the message is already visible, refresh its contents
       (drops items as the user fills them in) or hide it if the
       user completed everything. */
    var msg = document.querySelector('.ash-required-msg.is-visible');
    if (msg) {
      if (allSelected) hideMessage();
      else renderMessageContents(msg, missing);
    }
  }

  function getMissingSelections() {
    var missing = [];
    /* Variant fieldsets in the MAIN form only — quick-add forms on
       related-product cards are evaluated independently by Horizon. */
    var fieldsets = scopeQuery('fieldset.variant-option, fieldset[class*="variant-option--"]');
    fieldsets.forEach(function (fs) {
      var hasChecked = fs.querySelector('input[type="radio"]:checked');
      var hasSelect = fs.querySelector('select');
      var picked = hasChecked || (hasSelect && hasSelect.value !== '' && hasSelect.selectedIndex >= 0);
      if (!picked) {
        var name = fieldsetName(fs);
        if (missing.indexOf(name) === -1) missing.push(name);
      }
    });
    var groups = {};
    scopeQuery('input[data-ash-required]').forEach(function (r) {
      var token = r.getAttribute('data-ash-required');
      if (!(token in groups)) groups[token] = false;
      if (r.checked) groups[token] = true;
    });
    for (var token in groups) {
      if (!groups[token] && missing.indexOf(token) === -1) missing.push(token);
    }
    return missing;
  }

  function fieldsetName(fs) {
    var legend = fs.querySelector('legend');
    if (legend) {
      /* The Size Chart trigger (and any other helper UI) gets moved
         INTO the legend at runtime by ash-size-chart.liquid — using
         legend.textContent would return "Size Size Chart". Clone the
         legend, strip every link/button/helper-marked child, then read
         the text. */
      var clone = legend.cloneNode(true);
      clone.querySelectorAll(
        'a, button, [data-ash-sc-open], .ash-sc-trigger, .variant-option__size-chart, .variant-option__swatch-value'
      ).forEach(function (el) { el.remove(); });
      var t = (clone.textContent || '').trim();
      if (t) return t;
    }
    var aria = fs.getAttribute('aria-label');
    if (aria) return aria.trim();
    /* Pull from class like "variant-option--size" → "Size" */
    var m = (fs.className || '').match(/variant-option--([a-z0-9_-]+)/i);
    if (m) return m[1].charAt(0).toUpperCase() + m[1].slice(1);
    return 'Option';
  }

  function setButtonsDisabled(disabled) {
    /* Scope to the MAIN PDP buy-buttons row. We DON'T use the native
       `disabled` attribute because browsers suppress click events on
       disabled buttons — and we need the click to still fire so we
       can show the inline danger message. `aria-disabled` is semantic,
       `data-ash-disabled` is a marker our JS reads. */
    var buttons = scopeQuery(
      '.add-to-cart-button, button[ref="addToCartButton"], .ash-inline-buy-now, .ash-buy-now__btn'
    );
    buttons.forEach(function (b) {
      /* Always strip Horizon's `disabled` attribute — it stays even
         when `can_add_to_cart` flips. */
      b.removeAttribute('disabled');
      if (disabled) {
        b.setAttribute('aria-disabled', 'true');
        b.dataset.ashDisabled = '1';
      } else {
        b.removeAttribute('aria-disabled');
        delete b.dataset.ashDisabled;
      }
    });
  }

  function interceptAtcSubmit(e) {
    if (!e.target) return;
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    /* Skip quick-add forms on related-product cards. */
    if (form.closest('quick-add-component, product-card, .product-card')) return;
    var missing = getMissingSelections();
    if (missing.length === 0) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();
    var anchorBtn = form.querySelector('.add-to-cart-button, button[ref="addToCartButton"], .ash-inline-buy-now, .ash-buy-now__btn') || form;
    if (anchorBtn.classList && anchorBtn.classList.add) {
      anchorBtn.classList.add('ash-required-shake');
      setTimeout(function () { anchorBtn.classList.remove('ash-required-shake'); }, 350);
    }
    showMessage(missing, anchorBtn);
    return false;
  }

  function interceptAtcClick(e) {
    if (!e.target) return;
    var btn = e.target.closest('.add-to-cart-button, button[ref="addToCartButton"], .ash-inline-buy-now, .ash-buy-now__btn');
    if (!btn) return;
    /* Only intercept if this is a MAIN PDP button (not a quick-add
       button on a related-product card). */
    if (btn.closest('quick-add-component, product-card, .product-card')) return;
    /* Even if dataset.ashDisabled isn't set (e.g. mutation observer
       hasn't run yet), block the click whenever required selections
       are missing. The form-submit handler is the ultimate safety net,
       but blocking at click-time prevents the fly-to-cart animation
       from even starting. */
    var missing = getMissingSelections();
    if (missing.length > 0) {
      /* block the click entirely — stops Horizon's submit, stops the
         fly-to-cart animation, stops accelerated checkout. */
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      /* Visual nudge + inline danger message listing what's missing */
      btn.classList.add('ash-required-shake');
      setTimeout(function () { btn.classList.remove('ash-required-shake'); }, 350);
      /* Sticky-bar tap: the inline message lives near the inline buy
         row which is OFF SCREEN. Scroll the page up to the first
         missing fieldset and anchor the message there instead. */
      var isStickyTap = !!btn.closest('sticky-add-to-cart, .sticky-add-to-cart, .sticky-add-to-cart__bar');
      if (isStickyTap) {
        scrollToFirstMissing(missing);
      }
      showMessage(getMissingSelections(), btn);
      return false;
    }
  }

  /* Smooth-scroll the page to the first fieldset that still needs a
     selection. Used by the sticky ATC tap — without this the user
     gets blocked but has no visible cue what to fix because the
     inline error sits below the fold. */
  function scrollToFirstMissing(missing) {
    if (!missing || !missing.length) return;
    var first = missing[0];
    var target = null;
    /* Try variant fieldset first (legend text match). */
    var fieldsets = scopeQuery('fieldset.variant-option, fieldset[class*="variant-option--"]');
    for (var i = 0; i < fieldsets.length; i++) {
      if (fieldsetName(fieldsets[i]).toLowerCase() === first.toLowerCase()) {
        target = fieldsets[i];
        break;
      }
    }
    /* Fall back to any input carrying the matching data-ash-required token. */
    if (!target) {
      var ext = document.querySelector('[data-ash-required="' + first.replace(/"/g, '\\"') + '"]');
      if (ext) target = ext.closest('.ash-fit-selector, fieldset, [class*="ash-"]') || ext;
    }
    /* Last-resort: any variant fieldset at all. */
    if (!target && fieldsets[0]) target = fieldsets[0];
    if (!target) return;
    /* Offset for sticky header so the fieldset lands BELOW it. */
    var headerH = 0;
    var header = document.querySelector('header-component, .header, header');
    if (header) headerH = header.offsetHeight || 0;
    var rect = target.getBoundingClientRect();
    var top = (window.scrollY || window.pageYOffset) + rect.top - headerH - 24;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /* === Sticky bar: rebuild the variant line ourselves so it stays
        EMPTY until the user actually picks a variant option. Horizon
        SSRs the variant title (e.g. "Red / M") which is wrong while
        we've stripped the radios' checked state. Once a user picks,
        we render "<picked variant title>  •  Fit: Regular  •  …". */
  function refreshStickyExtras() {
    var bars = document.querySelectorAll('.sticky-add-to-cart');
    if (!bars.length) return;

    /* Has the user ACTUALLY picked any variant option yet? */
    var anyVariantPicked = !!document.querySelector(
      'fieldset.variant-option input[type="radio"]:checked, fieldset[class*="variant-option--"] input[type="radio"]:checked'
    );

    /* Compose the variant title from currently-checked radios so it
       matches Horizon's "/" join format (e.g. "Red / M"). */
    var variantParts = [];
    scopeQuery('fieldset.variant-option, fieldset[class*="variant-option--"]').forEach(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      var lab = checked.closest('label');
      var txt = '';
      if (lab) {
        var span = lab.querySelector('.variant-option__button-label__text, span:not([class*="pill"])') || lab;
        txt = (span.textContent || '').trim();
      }
      if (!txt) txt = checked.value || '';
      if (txt) variantParts.push(txt);
    });
    var variantTitle = variantParts.join(' / ');

    /* Collect selected extras across all data-ash-required groups. */
    var extras = [];
    var groups = {};
    scopeQuery('input[data-ash-required]').forEach(function (r) {
      var token = r.getAttribute('data-ash-required');
      if (!groups[token]) groups[token] = { token: token, value: '' };
      if (r.checked) {
        var label = r.closest('label');
        var txt = '';
        if (label) {
          var span = label.querySelector('.ash-fit-selector__option-label, span:not([class*="pill"])') || label;
          txt = (span.textContent || '').trim();
        }
        if (!txt) txt = r.value || '';
        groups[token].value = txt;
      }
    });
    for (var k in groups) {
      if (groups[k].value) extras.push(groups[k].token + ': ' + groups[k].value);
    }

    bars.forEach(function (bar) {
      var slot = bar.querySelector('.sticky-add-to-cart__variant');
      if (!slot) return;
      /* Take ownership of this slot so subsequent re-renders that
         try to write the SSR variant title back are overridden by
         our text on the next change/observer tick. */
      slot.dataset.ashManaged = '1';
      var parts = [];
      if (anyVariantPicked && variantTitle) parts.push(variantTitle);
      if (extras.length) parts = parts.concat(extras);
      var next = parts.join('  •  ');
      if (slot.textContent !== next) slot.textContent = next;
    });
  }

  /* Wipe the SSR variant title in the sticky bar exactly once,
     immediately, so the bar doesn't flash "Red / M" before the
     refreshStickyExtras debounce kicks in. */
  function clearStickyVariantOnLoad() {
    document.querySelectorAll('.sticky-add-to-cart .sticky-add-to-cart__variant').forEach(function (slot) {
      if (slot.dataset.ashCleared === '1') return;
      slot.dataset.ashCleared = '1';
      slot.textContent = '';
    });
  }

  /* Refresh on every relevant change + on a tiny debounce after
     Horizon's variant DOM mutations. */
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-ash-required], fieldset.variant-option input, fieldset[class*="variant-option--"] input')) {
      setTimeout(refreshStickyExtras, 0);
    }
  }, true);
  /* Clear immediately so no "Red / M" flashes; then refresh at the
     same timings the variant clearer runs (Horizon re-applies SSR
     state multiple times during boot). */
  function bootSticky() {
    clearStickyVariantOnLoad();
    refreshStickyExtras();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSticky);
  } else {
    bootSticky();
  }
  requestAnimationFrame(bootSticky);
  setTimeout(bootSticky, 60);
  setTimeout(bootSticky, 200);
  setTimeout(bootSticky, 500);
  setTimeout(bootSticky, 1000);

  function ensureMessageEl(anchorBtn) {
    var existing = document.querySelector('.ash-required-msg');
    if (existing) return existing;
    var el = document.createElement('div');
    el.className = 'ash-required-msg';
    el.setAttribute('role', 'alert');
    /* Place ABOVE the buy-buttons row — matches the position where
       Horizon's native "Cart Error" appears. */
    var row = anchorBtn.closest('.product-form-buttons, .ash-buy-row, form[action*="/cart/add"]');
    if (row && row.parentNode) {
      row.parentNode.insertBefore(el, row);
    } else {
      anchorBtn.parentNode.insertBefore(el, anchorBtn);
    }
    return el;
  }

  function showMessage(missing, anchorBtn) {
    if (!missing || missing.length === 0) { hideMessage(); return; }
    var el = ensureMessageEl(anchorBtn);
    renderMessageContents(el, missing);
    el.classList.add('is-visible');
  }

  function hideMessage() {
    var el = document.querySelector('.ash-required-msg');
    if (el) el.classList.remove('is-visible');
  }

  function renderMessageContents(el, missing) {
    var items = missing.map(function (m) { return '<li>' + escapeHtml(m) + '</li>'; }).join('');
    var html =
      '<span class="ash-required-msg__icon" aria-hidden="true">!</span>' +
      '<span class="ash-required-msg__text">Please select ' +
      '<ul class="ash-required-msg__list">' + items + '</ul>' +
      '</span>';
    /* Only write to the DOM if the markup is actually different —
       writing identical innerHTML still fires the MutationObserver
       and would cause an infinite re-render loop. */
    if (el.__ashLastHtml === html) return;
    el.__ashLastHtml = html;
    el.innerHTML = html;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
})();
